import { DealStatus } from '@/modules/deals/enums/DealStatus';
import { ProductType } from '@/commons/enums/ProductType';
import { DealStage } from '@/modules/deals/enums/DealStage';
import { DealLossReason } from '@/modules/deals/enums/DealLossReason';
import { chain, isArray, isNull, isObject, omitBy, pick } from 'lodash-es';
import Deal from '@/models/Deal';
import { LoanApplicationStepEnum } from '@/modules/loanApplication/enums/LoanApplicationStepEnum';
import { RegulationCategory } from '@/modules/regulation/enums/RegulationCategory';
import { ActivityKind } from '@/modules/activities/enums/ActivityKind';
import { AddressMeetingKind } from '@/commons/enums/AddressMeetingKind';
import { ActivityPriority } from '@/modules/activities/enums/ActivityPriority';
import { FilterGroup, IDataTableHeader } from '@/services/searchEngine/interfaces/DataTableHeader';
import { SortTypeEnum } from '@/modules/discussions/enums/SortTypeEnum';
import { Tag } from '@/models/Discussion';
import { ProspectStatus } from '@/modules/prospects/enums/ProspectStatus';
import { PlatformSection } from '@/models/enums/PlatformSection';
import { isDeepEmpty, Utils } from '@/services/utils/BasicUtils';
import { UserListElement } from '@/models/ppl/UserListElement';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';
import { SmsContextAction } from '@/commons/enums/SmsContextAction';
import DateUtils from '@/services/utils/DateUtils';
import { InterestRateType } from '@/commons/enums/InterestRateType';
import { BranchType } from '@/modules/branches/enums/BranchType';
import { Address } from '@/modules/multiForm/shared/MultiformModels';
import { isEmpty } from 'lodash-es';
import EnvironmentService from '@/env/EnvironmentService';
import { CrmModules } from '@/types/CrmModules';
import { LoanPurposeWhat } from './simulation/LoanPurposeWhat';
import { LoanPurposeActivity } from './simulation/LoanPurposeActivity';
import { banksDictionary } from '@/commons/dictionaries/BanksDictionary';
import { QuizCategory } from './Quiz';

export const DEFAULT_ROWS_PER_PAGE = 20;

export class BaseFilter {
  public page: number = 1;
  public descending: boolean = true;
  public rowsPerPage: number = DEFAULT_ROWS_PER_PAGE;
  public quantity?: number;
  public users: Array<number> = [];
  public existingUsers?: Array<UserListElement> = [];
  public name: Nullable<string> = null;
  public sortBy!: string | string[];
  public divisionIds?: Array<number> = [];

  public get totalPagesNumber(): number {
    if (this.quantity && this.quantity > 0) {
      return Math.ceil(this.quantity / this.rowsPerPage);
    }
    return this.page;
  }

  public get totalPagesDescription() {
    return `${I18NGetter().useFilter.PAGE} ${this.page} ${I18NGetter().useFilter.OF} ${this.totalPagesNumber}`;
  }

  private clearRecursively = (value: Object, nonClearAbleFields?: string[]) => {
    for (const key in value) {
      if (nonClearAbleFields?.includes(key)) continue
      if (value[key] instanceof Object && !(value[key] instanceof Date)) {
        this.clearRecursively(value[key]);
      } else {
        value[key] = null;
      }
    }
  }

  public clearFilterProp(value: keyof BaseFilter | Array<Record<keyof BaseFilter, BaseFilter>>, nonClearAbleFields?: string[]): void {
    const fields: (keyof BaseFilter)[] = this.getListOfFilterKeys(value);
    fields.forEach(key => {
      if (typeof this[key] === 'object' && typeof this[key] !== 'undefined' && !isEmpty(this[key]) && !Array.isArray(this[key])) {
        this.clearRecursively(this[key]!, nonClearAbleFields);
      } else if (Array.isArray(this[key])) {
        (this[key] as any) = [];
      } else {
        (this[key] as any) = null;
      }
    });
  }

  private getListOfFilterKeys(value: keyof BaseFilter | Array<Record<keyof BaseFilter, BaseFilter>>) {
    return Array.isArray(value)
      ? value.flatMap(el => (Object.keys(el) as (keyof BaseFilter)[]))
      : [value,];
  }

  public getValue(header: IDataTableHeader<any>): Record<keyof BaseFilter, BaseFilter>[] {
    const keys = header instanceof FilterGroup ? header.headers.map(el => el.valueFilter!) : header.valueFilter!;
    if (Array.isArray(keys)) {
      return keys
        .filter(key => {
          return !this.isHeaderFilterValueNull(key)
        })
        .map(key => {
          return pick(this, key) as any
        });
    } else {
      return this[keys] as any;
    }
  }

  public isHeaderFilterValueNull(key: keyof BaseFilter): boolean {
    const value: any = this[key];
    if (this.isValueObject(key)) {
      return isDeepEmpty(value);
    } else if (Array.isArray(value)) {
      return value.length === 0 && value.every((el: string | number | LRange<string>) => {
        if (el instanceof LRange) {
          return (el.to !== null || el.from !== null);
        } else {
          return el !== undefined && el !== null;
        }
      })
    } else return value === null || value === undefined;
  }

  // This function checks whether the filter has any value and if so whether it is only the nonClearAble one
  public isValueOnlyInNonClearableProperties(header: IDataTableHeader<any>): boolean {
    if (!this.isValueObject(header.valueFilter!)) {
      return false;
    }
    if (isDeepEmpty(this[header.valueFilter!] as Object)) {
      return false;
    }
    const properties = Object.entries(this[header.valueFilter!] || {});
    const propertiesWithValues = properties.filter(([key, value,]) => {
      if (typeof value === 'object' && !(value instanceof Date) && value !== null) {
        return isDeepEmpty(value);
      } else {
        return value !== null && value !== undefined && value !== '';
      }
    });
    if (propertiesWithValues.length === 0) {
      return false
    }
    return propertiesWithValues.every(([key, value,]: [key: string, value: Object]) => header.nonClearAbleProperties?.includes(key));
  }

  private isValueObject(key: keyof BaseFilter): boolean {
    return !Array.isArray(this[key]) && typeof this[key] === 'object' && this[key] !== null;
  }

  constructor(pageQuery?: string) {
    this.page = pageQuery ? parseInt(pageQuery) : 1;
  }

  get apiData(): BaseFilter {
    const data = chain(this)
      .clone()
      .thru((obj) => omitBy(obj, isNull))
      .thru((obj) => omitBy(obj, (x: LRange<Deal>) => isObject(x) && x.to === null && x.from === null))
      .thru((obj) => omitBy(obj, (x: Array<any>) => isArray(x) && x.length === 0))
      .value() as any as this;
    if (Array.isArray(data.sortBy)) {
      data.sortBy = data.sortBy[0];
    }
    if (data.existingUsers) {
      delete data.existingUsers;
    }
    return data;
  }
}

export class IssuesFilter extends BaseFilter {
  public productType: Nullable<ProductType> = ProductType.MORTGAGE;
  public bankSymbolsByType: Partial<Record<ProductType, Array<string>>> = (() => {
    return Object.values(ProductType).reduce((acc, type) => {
      acc[type] = banksDictionary().filter(bank => bank.products && bank.products[type]).map(bank => bank.type);
      return acc;
    }, {} as Record<ProductType, Array<string>>);
  })();
  public title: Nullable<string> = null;
  public bankSymbols?: Array<string> = [];
  constructor(filters?: Partial<IssuesFilter>) {
    super();
    if (filters) {
      Object.assign<IssuesFilter, Partial<IssuesFilter>>(this, filters);
    }
  }
}

export class LRange<T> {
  from: T | null = null;
  to: T | null = null;

  constructor(from?: T, to?: T) {
    if (from) {
      this.from = from;
    }
    if (to) {
      this.to = to;
    }
  }
}

export class PartnerCooperationFilter extends BaseFilter {
  public sortBy: string[] = ['name',];
  public name: Nullable<string> = null;
  public partnerId: Nullable<number> = null;
  public organisationId?: number | null;
  public divisionId?: number | null;
  public withoutDivisions?: boolean;
  public withoutOrganisations?: boolean;

  constructor(filters?: Partial<PartnerCooperationFilter>) {
    super();
    if (filters) {
      Object.assign<PartnerCooperationFilter, Partial<PartnerCooperationFilter>>(this, filters);
    }
  }
}

export class BranchFilter extends BaseFilter {
  public sortBy: string[] = ['name',];
  public id: Nullable<number> = null;
  public name: Nullable<string> = null;
  public branchType: Nullable<string> = null;
  public updatedAt: LRange<string> = new LRange<string>();

  constructor(filters?: Partial<BranchFilter>) {
    super();
    if (filters) {
      Utils.assign<BranchFilter, Partial<BranchFilter>>(this, filters);
    }
  }
}

export class VirtualLocationFilter extends BaseFilter {
  public descending: boolean = false;
  public sortBy: string[] = ['city',];
  public city: Nullable<string> = null;

  constructor(filters?: Partial<VirtualLocationFilter>) {
    super();
    if (filters) {
      Utils.assign<VirtualLocationFilter, Partial<VirtualLocationFilter>>(this, filters);
    }
  }
}

export class ProspectHistoryFilter extends BaseFilter {
  public rowsPerPage: number = 10;

  constructor(filters?: Partial<ProspectHistoryFilter>) {
    super();
    if (filters) {
      Utils.assign<ProspectHistoryFilter, Partial<ProspectHistoryFilter>>(this, filters);
    }
  }
}

export class PartnerFilter extends BaseFilter {
  public sortBy: string[] = ['name',];
  public name: Nullable<string> = null;
  public email: Nullable<string> = null;
  public nip: Nullable<string> = null;
  public regon: Nullable<string> = null;
  public partnerTypes: string[] = [];
  public krs: Nullable<string> = null;
  public expertFit: boolean = false;
  public addedAt: LRange<string> = new LRange<string>();
  public modifiedAt: LRange<string> = new LRange<string>();
  public ownerId: Nullable<number> = null
  public primaryAddress: Nullable<Address> = EnvironmentService.Environment.getAppDomainConfig().moduleSettings[CrmModules.PARTNERS].availableFilters.includes('primaryAddress') ? new Address() : null;

  public status: Nullable<string> = null;

  constructor(filters?: Partial<PartnerFilter>) {
    super();
    if (filters) {
      Object.assign<PartnerFilter, Partial<PartnerFilter>>(this, filters);
    }
  }
}

export class ClientFilter extends BaseFilter {
  public sortBy: string[] = ['addedAt',];
  public pesel: Nullable<string> = null;
  public nip: Nullable<string> = null;
  public regon: Nullable<string> = null;
  public firstName: Nullable<string> = null;
  public lastName: Nullable<string> = null;
  public email: Nullable<string> = null;
  public phone: Nullable<string> = null;
  public addedAt: LRange<string> = new LRange<string>();
  public modifiedAt: LRange<string> = new LRange<string>();
  public isCompany?: boolean = undefined;
  public id: number | null = null;
  public acceptsProcessing: Nullable<boolean> = null;
  public acceptsReceivingInfo: Nullable<boolean> = null;
  public acceptsReceivingOffers: Nullable<boolean> = null;
  public acceptsReceivingInfoPartner: Nullable<boolean> = null;
  public acceptsProcessingPartner: Nullable<boolean> = null;
  public acceptsReceivingOffersPartner: Nullable<boolean> = null;

  constructor(filters?: Partial<ClientFilter>) {
    super();
    if (filters) {
      Object.assign<ClientFilter, Partial<ClientFilter>>(this, filters);
    }
  }
}

export class UserFilter extends BaseFilter {
  public sortBy: string[] = ['addedAt',];
  public username: Nullable<string> = null;

  public isForProspects: Nullable<boolean> = null;
  public isActive: Nullable<boolean> = null;
  public name: Nullable<string> = null;
  public search: Nullable<string> = null;
  public email: Nullable<string> = null;

  public organisationIds: Array<number> = [];

  public phone: Nullable<string> = null;
  public pesel: Nullable<string> = null;
  public division: Nullable<string> = null;
  public id?: number;

  public simplePartnerView?: boolean;
  defaultPartnerId: number | null = null;

  constructor(filters?: Partial<UserFilter>) {
    super();
    if (filters) {
      Object.assign<UserFilter, Partial<UserFilter>>(this, filters);
    }
  }
}

export class QuizAttemptFilter extends BaseFilter {
  public sortBy: string = 'takenAt';
  public isHrLocked: Nullable<boolean> = null
  public isContractOfMandate: Nullable<boolean> = null;
  public category: Nullable<QuizCategory> = null;

  constructor(filters?: Partial<QuizAttemptFilter>) {
    super();
    if (filters) {
      Object.assign<QuizAttemptFilter, Partial<QuizAttemptFilter>>(this, filters);
    }
  }
}

export class DelegationListsFilter extends BaseFilter {
  public name: Nullable<string> = null;
  public users: Array<number> = [];
}

export class ProspectFilter extends BaseFilter {
  public sortBy: string[] = ['deal.created_at',];

  public contactCreatedAt: LRange<Date> = new LRange<Date>();
  public dealCreatedAt: LRange<Date> = new LRange<Date>();
  public dealUpdatedAt: LRange<Date> = new LRange<Date>();
  public activityAt: LRange<Date> = new LRange<Date>();
  public phone: Nullable<string> = null;
  public email: Nullable<string> = null;
  public voivodship: Nullable<string> = null;
  public sourceName: Nullable<string> = null;
  public status: Array<ProspectStatus> = [];

  constructor(filters?: Partial<ProspectFilter>) {
    super();
    if (filters) {
      Object.assign<ProspectFilter, Partial<ProspectFilter>>(this, filters);
    }
  }
}

export class DealsFilter extends BaseFilter {
  public sortBy: (keyof Deal)[] = ['updatedAt',];
  public bankApplicationNumber: Nullable<string> = null;
  public bankContractNumber: Nullable<string> = null;
  public productName: Nullable<string> = null;
  public statuses: Array<DealStatus> = [];
  public productTypes: Array<ProductType> = [];
  public stages: Array<DealStage> = [DealStage.WON, DealStage.PENDING,];
  public loanAmount: LRange<number> = new LRange<number>();
  public lossReasons: Array<DealLossReason> = [];
  public bankMargin: LRange<number> = new LRange<number>();
  public bankProvision: LRange<number> = new LRange<number>();
  public propertyValue: LRange<number> = new LRange<number>();
  public remuneration: LRange<number> = new LRange<number>();
  public consultantCommission: LRange<number> = new LRange<number>();
  public brokerOrganisationId: LRange<number> = new LRange<number>();
  public isRodoDocumentsAvailable: Nullable<boolean> = null;
  public isOfiDocumentsAvailable: Nullable<boolean> = null;
  public isInformationFormDocumentAvailable: Nullable<boolean> = null;
  public isSubsequentInformationFormsDocumentAvailable: Nullable<boolean> = null;
  public isDecisionDocumentAvailable: Nullable<boolean> = null;
  public isAgreementDocumentAvailable: Nullable<boolean> = null;
  public isFromCC: Nullable<boolean> = null;
  public bankIds: Array<number> = [];
  public createdAt: LRange<string | Date> = new LRange<string | Date>();
  public updatedAt: LRange<string | Date> = new LRange<string | Date>();
  public submittedAt: LRange<string | Date> = new LRange<string | Date>();
  public createdDate: LRange<string> = new LRange<string>();
  public wonAt: LRange<string> = new LRange<string>();
  public realisationAt: LRange<string> = new LRange<string>();
  public partners: Array<number> = [];
  public clients: Array<number> = [];
  public organisationId: Array<number> = [];
  public interestRateType: Nullable<InterestRateType> = null;
  public id: number | null = null;
  public showTraces: boolean = false;
  public duration: LRange<number> = new LRange<number>();
  public nextMeetingActivity: LRange<string | Date> = new LRange<string | Date>();

  constructor(filters?: Partial<DealsFilter>) {
    super();
    if (filters) {
      Object.assign<DealsFilter, Partial<DealsFilter>>(this, filters);
    }

    if (this.createdAt.from) {
      this.createdAt.from = new Date(this.createdAt.from);
    }
    if (this.createdAt.to) {
      this.createdAt.to = new Date(this.createdAt.to);
    }
    if (this.updatedAt.from) {
      this.updatedAt.from = new Date(this.updatedAt.from);
    }
    if (this.updatedAt.to) {
      this.updatedAt.to = new Date(this.updatedAt.to);
    }
    if (this.nextMeetingActivity.from) {
      this.nextMeetingActivity.from = new Date(this.nextMeetingActivity.from);
    }
    if (this.nextMeetingActivity.to) {
      this.nextMeetingActivity.to = new Date(this.nextMeetingActivity.to);
    }
  }
}

export class ReportFilter extends DealsFilter {
  public stages: Array<DealStage> = [DealStage.WON,];
  public settledApplicationSteps: Array<number> = [];
  public isReport: boolean = true;
  public partnerTypes: string[] = [];
  public loanPurposeWhat: Array<LoanPurposeWhat> | undefined = EnvironmentService.Environment.getAppDomainConfig().moduleSettings.REPORTS.loanPurposeWhatConfig.hasLoanPurposeWhat ? [] : undefined;
  public loanPurposeActivity: Array<LoanPurposeActivity> | undefined = EnvironmentService.Environment.getAppDomainConfig().moduleSettings.REPORTS.hasLoanPurposeActivity ? [] : undefined;

  constructor(filters?: Partial<ReportFilter>) {
    super(filters);
    if (filters) {
      if (filters?.existingUsers) {
        filters.existingUsers = filters?.existingUsers.map(x => new UserListElement(x));
      }
      Utils.assign<ReportFilter, Partial<ReportFilter>>(this, filters);
    }
  }
}

export class RegulationFilter extends BaseFilter {
  public sortBy: Array<string> = ['createdDate',];
  public categoriesIds: Array<RegulationCategory> = [];
  public bankIds: Array<number> = [];
  public title: Nullable<string> = null;
  public onlyFavorites: Nullable<boolean> = false;
  public createdDate: LRange<string> = new LRange<string>();

  constructor(filters?: Partial<RegulationFilter>) {
    super();
    if (filters) {
      Object.assign<RegulationFilter, Partial<RegulationFilter>>(this, filters);
    }
  }
}

export class BigFilter extends BaseFilter {
  public clientId: Array<number> = [];
  public userId: Array<number> = [];
  public createdAt: LRange<string> = new LRange<string>();
  public isAccepted?: boolean;
  public isPending?: boolean;

  constructor(filters?: Partial<BigFilter>) {
    super();
    if (filters) {
      Object.assign<BigFilter, Partial<BigFilter>>(this, filters);
    }
  }
}

export class FiltersResponse<T> {
  items!: Array<T>;
  quantity!: number;
  page?: number;
}

export class ReportFiltersResponse extends FiltersResponse<ReportFilter> {
  loanAmountSum?: number;
  delegatedLoanAmountSum?: number;
}

export class LoanApplicationFilter extends BaseFilter {
  // public provision: FilterHelperObject<number> = FilterHelperObject<number>();
  // public productTypes: Array<LoanApplicationCategory> = [];
  public sortBy: string[] = ['modifiedAt',];
  public bankIds: Array<number> = [];
  public stepsIds: Array<LoanApplicationStepEnum> = [];
  public clients: Array<number> = [];
  public dealName: Nullable<string> = null;
  public amount: LRange<number> = new LRange<number>();
  public realisationDate: LRange<string> = new LRange<string>();
  public modifiedAt: LRange<string> = new LRange<string>();
  public startedAt: LRange<string> = new LRange<string>();

  constructor(filters?: Partial<LoanApplicationFilter>) {
    super();
    if (filters) {
      Object.assign<LoanApplicationFilter, Partial<LoanApplicationFilter>>(this, filters);
    }
  }
}

export class ActivityFilter extends BaseFilter {
  public kind: Array<ActivityKind> = [];
  public text: Nullable<string> = null;
  public dealId: Array<number> = [];
  public clientId: Array<number> = [];
  public createdBy: Array<number> = [];
  public assignedTo: Array<number> = [];
  public inDescription: boolean = false;
  public isFullDate: boolean = false;
  public addressId: Nullable<number> = null;
  public paginate: boolean = true;
  public addressMeetingKind: Nullable<AddressMeetingKind> = null;
  public priority: Nullable<ActivityPriority> = null;
  public createdAt: LRange<string> = new LRange<string>();
  public isBetween: LRange<string> = new LRange<string>();
  public startedDate: LRange<string> = new LRange<string>();
  public endDate: LRange<string> = new LRange<string>();
  public divisionIds?: Array<number> = undefined;

  constructor(filters?: Partial<ActivityFilter>) {
    super();
    if (filters) {
      Object.assign<ActivityFilter, Partial<ActivityFilter>>(this, filters);
    }
  }
}

export class DiscussionFilter {
  public page: number = 1;
  public title: Nullable<string> = null;
  public tags: Nullable<Array<Tag>> = null;
  public sortBy: SortTypeEnum = SortTypeEnum.DATE;
}

export class TrainingFilter extends BaseFilter {
  public page: number = 1;
  public rowsPerPage: number = 20;
  public category: Nullable<string> = null;
  public bankIds: Nullable<Array<number>> = null;
  public platformSection: Nullable<PlatformSection> = null;
  public startDate: LRange<string> = new LRange<string>();
  public sortBy: string = 'startDate';

  constructor(filters?: Partial<TrainingFilter>) {
    super();
    if (filters) {
      Object.assign<TrainingFilter, Partial<TrainingFilter>>(this, filters);
    }
  }
}

export class DivisionFilter extends BaseFilter {
  public sortBy: string = 'addedAt';
  public organisationId: Nullable<number> = null;
  public chiefIds: number[] = [];
  public updated?: boolean;
  public search?: string;
  public typeIds?: number[];
  public isActive?: boolean;
  public nip?: string;
  constructor(filters?: Partial<DivisionFilter>) {
    super();
    if (filters) {
      Object.assign<DivisionFilter, Partial<DivisionFilter>>(this, filters);
    }
  }
}

export class SmsLogFilter extends BaseFilter {
  public descending: boolean = true;
  public sortBy: string = 'createdAt';
  public phone: Nullable<string> = null;
  public message: Nullable<string> = null;
  public createdAt: LRange<string> = new LRange<string>();
  public contextAction: Nullable<SmsContextAction> = null;

  constructor(filters?: Partial<SmsLogFilter>) {
    super();
    if (filters) {
      Object.assign<SmsLogFilter, Partial<SmsLogFilter>>(this, filters);
    }
  }
}

export class EmailLogFilter extends BaseFilter {
  public subject: Nullable<string> = null;
  public sentTo: Nullable<string> = null;
  public isSent: Nullable<boolean> = null;
  public from: Nullable<string> = null;
  public emailText: Nullable<string> = null;
  public range: LRange<string> = new LRange<string>();

  constructor(filters?: Partial<EmailLogFilter>) {
    super();
    if (filters) {
      Object.assign<EmailLogFilter, Partial<EmailLogFilter>>(this, filters);
    }
  }
}

export class NPSFilter extends BaseFilter {
  public quantity?: number | undefined = 0;
  public sortBy: string = 'createdAt';
  public userIds?: number[];
  public createdAt?: LRange<string | Date> = new LRange<string | Date>();
  public rating?: LRange<number> = new LRange<number>();
  public branchType?: BranchType | string;
  public branchIds?: number[];
  public clientPhone: string | null = null;
  public hasTextOpinion?: boolean;
  public sourceType?: string;
}

export class MandateContractUserFilters extends BaseFilter {
  public clientCreatedAt: LRange<Date> = new LRange<Date>(
    DateUtils.getFirstDayOfCurrentMonth(),
    DateUtils.getLastDayOfCurrentMonth()
  );

  public pesel?: Nullable<string> = null;
  public sortBy: string = '';
  // public id?: string;

  constructor(filters?: Partial<MandateContractUserFilters>) {
    super();
    if (filters) {
      Object.assign<MandateContractUserFilters, Partial<MandateContractUserFilters>>(this, filters);
    }
  }
}
